/** @jsx jsx */
import { Fragment, useState } from 'react';
import { jsx, css } from '@emotion/core';

import {
  Checkbox,
  Document,
  Link,
  MessageBar,
  Text,
} from '@zapier/common-components';
import { SkeletonBlock, Spacer } from '@zapier/design-system';
import { useLink } from '@zapier/common-hooks';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import {
  useCurrentAccountId,
  usePlans,
  useZapsToBePaused,
} from '@zapier/design-system-revenue/src/hooks';

import {
  ConfirmSection,
  DowngradeForm,
} from '@zapier/design-system-revenue/src/components/other';

import { Interval, PlanV2 } from '@zapier/design-system-revenue/src/types';

import { ExplanationFeatureLine } from './ExplanationFeatureLine';
import { buildDowngradeSurveyUrl, getFeaturesLost } from './utils';

const Styles = {
  checkboxText: css`
    margin-left: 15px;
  `,
};

type ConfirmDowngradeProps = {
  params: {
    fromPlanName: string;
    toPlanName: string;
    interval: Interval;
  };
};

export const ConfirmDowngrade = ({ params }: ConfirmDowngradeProps) => {
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();
  const [understandsDowngrade, setUnderstandsDowngrade] = useState(false);
  const { toPlanName, interval } = params;

  // Data fetching ----------------------------------
  const { accountId } = useCurrentAccountId();
  const {
    data: { plans, currentPlan },
    isLoading: isPlansLoading,
  } = usePlans(accountId, { flatPlans: true });

  const toPlan = (plans as PlanV2[]).find(
    p => toPlanName.toLowerCase() === p.name.toLowerCase()
  );
  const { zaps: zapsToBePaused, isLoading: isZapsLoading } = useZapsToBePaused({
    accountId,
    planId: toPlan?.id,
  });

  const featuresLost = getFeaturesLost(currentPlan, toPlan);
  const isLoading = isPlansLoading || isZapsLoading;

  // Click handlers ---------------------------------
  const onConfirm = () => {
    if (routeTo) {
      return routeTo(
        buildDowngradeSurveyUrl(currentPlan?.name ?? '', toPlanName, interval)
      );
    }
    return null;
  };

  const onCancel = () => {
    trackEvent('Billing: cancel downgrade', {
      accountId,
      fromPlanName: currentPlan?.name,
      plan: toPlanName,
      interval,
      step: 'are you sure',
    });
    if (routeTo) {
      return routeTo('/app/zaps');
    }
    return null;
  };

  const lostFeatureLines = featuresLost.map(feature => (
    <ExplanationFeatureLine
      href={feature.helpUrl}
      key={feature.name}
      linkText={feature.helpText}
      name={feature.name}
    />
  ));

  const pauseZapsWarning =
    zapsToBePaused.length === 0 ? null : (
      <MessageBar type="warning">
        {zapsToBePaused.length} of your Zaps will be turned off. We recommend
        going to{' '}
        <Link href="/app/zaps" underline="always">
          your Zaps
        </Link>{' '}
        to edit your Zaps.
      </MessageBar>
    );

  return (
    <Document title="Confirm Downgrade Plan">
      {isLoading ? (
        <SkeletonBlock />
      ) : (
        <DowngradeForm header="Are You Sure?" planName={toPlanName}>
          <div>
            {pauseZapsWarning}
            <Spacer height={20} />

            {lostFeatureLines.length === 0 ? null : (
              <Fragment>
                <Text type="subsection-head">
                  Premium features you’re losing
                </Text>

                <Spacer height={20} />

                {featuresLost.map(feature => (
                  <ExplanationFeatureLine
                    href={feature.helpUrl}
                    key={feature.name}
                    linkText={feature.helpText}
                    name={feature.name}
                  />
                ))}

                <Spacer height={20} />

                <label>
                  <Checkbox
                    aria-label="understands-downgrade"
                    checked={understandsDowngrade}
                    name="understands-downgrade"
                    onChange={() =>
                      setUnderstandsDowngrade(!understandsDowngrade)
                    }
                    value="understands-downgrade"
                  />

                  <span css={Styles.checkboxText}>
                    I understand I will lose access to the above features{' '}
                    <Text type="body-strong">immediately</Text> and I still want
                    to downgrade.
                  </span>
                </label>
              </Fragment>
            )}

            <Spacer height={20} />

            <ConfirmSection
              cancelText="Go to Dashboard"
              confirmText="Continue"
              isConfirmDisabled={
                lostFeatureLines.length > 0 && !understandsDowngrade
              }
              onCancel={onCancel}
              onConfirm={onConfirm}
            />
          </div>
        </DowngradeForm>
      )}
    </Document>
  );
};
